import { css } from "@emotion/css";
import Skeleton from "@mui/material/Skeleton";
import { Edit, FileText, MoreVertical, Trash2 } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useHistory, useParams } from "react-router-dom";
import routing from "routing";
import Image from "src/components/Default/Image";
import { PathBackButton } from "src/components/Default/LekUI/BackButton";
import { Menu } from "src/components/Default/LekUI/Menu";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";
import { LayoutWithSearch } from "src/containers/Layouts/LayoutWithSearch";
import { useDeleteUserNote, useGetUserNote } from "src/hooks/useFolders";
import "src/styles/notes.scss";

interface LightboxState {
	open: boolean;
	images: { image: string }[];
	index: number;
}

const CLOSED_LIGHTBOX: LightboxState = { open: false, images: [], index: 0 };

export const NotePreview = () => {
	const { t } = useTranslation();
	const history = useHistory();
	const { id: folderId, noteId } = useParams<{ id: string; noteId: string }>();
	const { openModal } = useModals();
	const contentRef = useRef<HTMLDivElement | null>(null);
	const [lightbox, setLightbox] = useState<LightboxState>(CLOSED_LIGHTBOX);

	const handleContentRef = useCallback((node: HTMLDivElement | null) => {
		contentRef.current = node;
		if (!node) return;

		const images = node.querySelectorAll<HTMLImageElement>("img");
		for (const img of images) {
			img.style.cursor = "pointer";
			img.addEventListener("click", () => {
				const allImages = Array.from(
					node.querySelectorAll<HTMLImageElement>("img"),
				);
				const clickedIndex = allImages.indexOf(img);
				setLightbox({
					open: true,
					images: allImages.map((i) => ({ image: i.src })),
					index: clickedIndex,
				});
			});
		}
	}, []);

	const { data: noteData, isLoading } = useGetUserNote(noteId) as {
		data?: { name?: string; note?: string };
		isLoading: boolean;
	};
	const { mutateAsync: deleteNote } = useDeleteUserNote();

	const handleEdit = () => {
		history.push(
			generatePath(routing.paths.editNote, { id: folderId, noteId }),
		);
	};

	const handleDelete = () => {
		openModal(ModalsNames.DeleteItemModal, {
			title: t("modals.deleteItem.note.title", "Usunąć notatkę?"),
			description: t(
				"modals.deleteItem.note.description",
				"Notatka zostanie trwale usunięta. Tej operacji nie można cofnąć.",
			),
			confirmText: t("modals.deleteItem.note.confirm", "Usuń notatkę"),
			itemLabel: noteData?.name,
			onDelete: async () => {
				await deleteNote(noteId);
				history.push(generatePath(routing.paths.folder, { id: folderId }));
			},
		});
	};

	if (isLoading) {
		return (
			<LayoutWithSearch>
				<div className="flex justify-center">
					<div className="w-100">
						<div className="border-medium-grey bg-white border-radius-16 p-24">
							<div className="mb-16">
								<Skeleton
									variant="text"
									width={120}
									height={20}
									className="mb-8"
								/>
								<Skeleton variant="rounded" width={350} height={48} />
							</div>
							<div className="border-top-1 my-16" />
							<Skeleton variant="rounded" height={300} />
						</div>
					</div>
				</div>
			</LayoutWithSearch>
		);
	}

	return (
		<LayoutWithSearch>
			<div className="flex justify-center">
				<div className="w-100">
					<div className="note-preview-card">
						<div className="flex flex-col gap-8 mb-16">
							<div className="flex items-center justify-between">
								<PathBackButton />
								<Menu
									icon={<MoreVertical className="icon-normal" />}
									buttonClassName="square-32"
									buttonTestId="note-actions-menu-button"
									menuPopperClassName={css`min-width: 150px;`}
									closeAfterClick
									items={[
										{
											title: t("commands.edit", "Edytuj"),
											onClick: handleEdit,
											Icon: <Edit className="icon-small" />,
											testId: "note-menu-edit-item",
										},
										{
											title: t("commands.delete", "Usuń"),
											onClick: handleDelete,
											Icon: <Trash2 className="icon-small icon-error" />,
											itemTextClassName: "text-error",
											testId: "note-menu-delete-item",
										},
									]}
								/>
							</div>
							<div className="flex items-center gap-8 pr-8">
								<FileText className="icon-small" />
								<h2 className="m-0 text-ellipsis-1 min-w-0 flex-1">
									{noteData?.name || t("note.untitled", "Notatka bez tytułu")}
								</h2>
							</div>
						</div>
						<div className="border-top-1 my-16" />
						<div className="tiptap-editor-content">
							<div
								ref={handleContentRef}
								className="tiptap"
								dangerouslySetInnerHTML={{
									__html: noteData?.note || "",
								}}
							/>
						</div>
					</div>
				</div>
			</div>
			{lightbox.open && (
				<Image
					images={lightbox.images}
					index={lightbox.index}
					close={() => setLightbox(CLOSED_LIGHTBOX)}
				/>
			)}
		</LayoutWithSearch>
	);
};
